// src/features/session/parts/AffektraPortal.tsx
import "./AffektraPortal.css";
import React, { useMemo, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { Trash2, Play, Square, X, ChevronDown, Lightbulb, Target, ListPlus, Sparkles, Loader2, ThumbsUp, ThumbsDown } from "lucide-react";
import ConfirmDialog from "../../../components/ConfirmDialog";
import { Button } from "../../../components/ui/Button";

// Card types from agent worker
export type AgentCardType = "insight" | "add_board" | "add_goal" | "analysis";

export type AgentCard = {
  id: string;
  type: AgentCardType;
  title: string;
  body?: string;
  action_data?: {
    prompt_id?: string;
    prompt_title?: string;
    board_title?: string;
    board_desc?: string;
    goal_title?: string;
    goal_desc?: string;
  };
  // UI state (not from backend)
  status?: "idle" | "running" | "ready" | "error";
  result?: string;
};

// Legacy type for backwards compatibility
export type AffektraCard = {
  id: string;
  title: string;
  body?: string;
};

export type AffektraActionCtx = {
  cardId?: string;
  actionId: string;
  patientId?: string;
  sessionId?: string;
  maxChunkIndex?: number;
  replay?: boolean;
};

export type AffektraRunStatus = "idle" | "running" | "stopping" | "error";

export type CardFeedback = "like" | "dislike";

export type CardFeedbackCtx = {
  cardId: string;
  cardType: AgentCardType;
  cardTitle: string;
  feedback: CardFeedback;
  sessionId?: string;
  patientId?: string;
};

type AffektraPortalProps = {
  panelRef: React.RefObject<HTMLDivElement | null>;
  open: boolean;
  onClose: () => void;
  cards: (AgentCard | AffektraCard)[];

  runStatus?: AffektraRunStatus;
  errorText?: string;

  // Actions
  onRunAction: (cardId: string, actionId: string) => void;
  onRunActionEx?: (ctx: AffektraActionCtx) => void;
  onExecuteCard?: (card: AgentCard) => Promise<string | void>;
  onCardFeedback?: (ctx: CardFeedbackCtx) => void;

  patientId?: string;
  sessionId?: string;
  sessionSummary?: string;
};

// Normalize cards to AgentCard format
function normalizeCard(card: AgentCard | AffektraCard): AgentCard {
  if ("type" in card && card.type) {
    return card as AgentCard;
  }
  // Legacy card format
  return {
    id: card.id,
    type: "insight",
    title: card.title,
    body: card.body,
  };
}

// Card type icons
const CardIcon: React.FC<{ type: AgentCardType; size?: number }> = ({ type, size = 16 }) => {
  switch (type) {
    case "insight":
      return <Lightbulb size={size} />;
    case "add_board":
      return <Target size={size} />;
    case "add_goal":
      return <ListPlus size={size} />;
    case "analysis":
      return <Sparkles size={size} />;
    default:
      return <Lightbulb size={size} />;
  }
};

// Card type labels - will be translated in component
const cardTypeLabelKeys: Record<AgentCardType, string> = {
  insight: "affektra.insight",
  add_board: "affektra.newBoard",
  add_goal: "affektra.newGoal",
  analysis: "affektra.analysis",
};

function clampPreview(text: string, maxChars = 220): string {
  const t = (text || "").trim().replace(/\s+/g, " ");
  if (!t) return "";
  if (t.length <= maxChars) return t;
  return t.slice(0, maxChars).trimEnd() + "…";
}

export function AffektraPortal({
  open,
  onClose,
  cards,
  runStatus = "idle",
  errorText = "",
  onRunAction,
  onRunActionEx,
  onExecuteCard,
  onCardFeedback,
  patientId,
  sessionId,
}: AffektraPortalProps) {
  const { t } = useTranslation();
  const ids = useMemo(
    () => ({
      patientId: typeof patientId === "string" && patientId.trim() ? patientId : undefined,
      sessionId: typeof sessionId === "string" && sessionId.trim() ? sessionId : undefined,
    }),
    [patientId, sessionId]
  );

  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [cardStates, setCardStates] = useState<Record<string, { status: string; result?: string }>>({});
  const [drawerCard, setDrawerCard] = useState<AgentCard | null>(null);
  const [confirmClear, setConfirmClear] = useState(false);
  const [cardFeedbacks, setCardFeedbacks] = useState<Record<string, CardFeedback>>({});

  // Handle feedback submission
  const handleFeedback = useCallback((card: AgentCard, feedback: CardFeedback) => {
    // Update local state immediately
    setCardFeedbacks(prev => ({ ...prev, [card.id]: feedback }));

    // Call callback if provided
    if (onCardFeedback) {
      onCardFeedback({
        cardId: card.id,
        cardType: card.type,
        cardTitle: card.title,
        feedback,
        sessionId: ids.sessionId,
        patientId: ids.patientId,
      });
    }
  }, [onCardFeedback, ids]);

  const normalizedCards = useMemo(() => {
    if (!Array.isArray(cards)) return [];
    return cards.map(normalizeCard);
  }, [cards]);

  const hasCards = normalizedCards.length > 0;

  const run = useCallback(
    (cardId: string, actionId: string) => {
      try {
        onRunAction(cardId, actionId);
      } catch (e) {
        console.error("[AffektraPortal] onRunAction error:", e);
      }
      if (onRunActionEx) {
        try {
          onRunActionEx({ cardId, actionId, ...ids });
        } catch (e) {
          console.error("[AffektraPortal] onRunActionEx error:", e);
        }
      }
    },
    [onRunAction, onRunActionEx, ids]
  );

  // Execute an actionable card
  const handleExecuteCard = useCallback(
    async (card: AgentCard) => {
      if (!onExecuteCard) return;

      setCardStates((prev) => ({
        ...prev,
        [card.id]: { status: "running" },
      }));

      try {
        const result = await onExecuteCard(card);
        setCardStates((prev) => ({
          ...prev,
          [card.id]: { status: "ready", result: result || "Completed successfully" },
        }));
        // Open drawer with result
        if (result) {
          setDrawerCard({ ...card, status: "ready", result });
        }
      } catch (e: any) {
        setCardStates((prev) => ({
          ...prev,
          [card.id]: { status: "error", result: e?.message || "Execution failed" },
        }));
      }
    },
    [onExecuteCard]
  );

  if (!open) return null;

  const showRunning = runStatus === "running" || runStatus === "stopping";
  const showError = runStatus === "error" || !!errorText;

  return (
    <div className="afp-root-inline">
      {/* Header */}
      <div className="afp-header">
        <span className="afp-title">{t("affektra.title")}</span>
      </div>

      {/* Toolbar row */}
      <div className="afp-toolbar-row">
        <Button
          variant="ghost"
          iconOnly
          size="sm"
          onClick={() => run("global", "play")}
          aria-label={t("common.run")}
          title={showRunning ? t("common.running") : t("affektra.runAgent")}
          disabled={runStatus === "running"}
        >
          {showRunning ? <Loader2 size={16} strokeWidth={2} className="afp-spin" /> : <Play size={16} strokeWidth={2} />}
        </Button>

        <Button
          variant="ghost"
          iconOnly
          size="sm"
          onClick={() => run("global", "stop")}
          aria-label={t("common.stop")}
          title={t("affektra.stop")}
          disabled={runStatus !== "running" && runStatus !== "stopping"}
        >
          <Square size={16} strokeWidth={2} />
        </Button>

        <Button
          variant="ghost"
          iconOnly
          size="sm"
          onClick={() => setConfirmClear(true)}
          aria-label={t("affektra.clearOutputs")}
          title={t("affektra.clearOutputs")}
          disabled={runStatus === "running" || runStatus === "stopping"}
        >
          <Trash2 size={16} strokeWidth={2} />
        </Button>

        {showRunning && (
          <span className="afp-status-pill afp-status-pill--running">
            <Loader2 size={12} className="afp-spin" /> {runStatus === "stopping" ? t("common.stopping") : t("common.running")}
          </span>
        )}

        {showError && (
          <span className="afp-status-pill afp-status-pill--error" title={errorText || "Error"}>
            ⚠ {errorText || "Error"}
          </span>
        )}
      </div>

      {/* Body */}
      <div className="afp-body">
        {!hasCards && showRunning && (
          <div className="afp-loading-state">
            <div className="afp-loading-icon">
              <Loader2 size={32} className="afp-spin" />
            </div>
            <div className="afp-loading-text">{t("affektra.analyzing")}</div>
            <div className="afp-loading-subtext">{t("affektra.analyzingSubtext")}</div>
          </div>
        )}

        {!hasCards && !showRunning && (
          <div className="afp-empty">
            {t("affektra.emptyState").split("\n").map((line, i) => (
              <React.Fragment key={i}>{line}{i === 0 && <br />}</React.Fragment>
            ))}
          </div>
        )}

        {hasCards && (
          <div className="afp-list">
            {normalizedCards.map((card, ci) => {
              const isOpen = expandedId === card.id;
              const preview = clampPreview(card.body || "", 220);
              const cardState = cardStates[card.id] || { status: "idle" };
              const isActionable = card.type !== "insight";
              const isRunning = cardState.status === "running";
              const isReady = cardState.status === "ready";

              return (
                <div
                  key={card.id}
                  className={`afp-card afp-card--${card.type} ${isRunning ? "afp-card--running" : ""} ${isReady ? "afp-card--ready" : ""}`}
                >
                  {/* Card Header */}
                  <div className="afp-card-header">
                    <div className="afp-card-type">
                      <CardIcon type={card.type} size={14} />
                      <span className="afp-card-type-label">{t(cardTypeLabelKeys[card.type])}</span>
                    </div>

                    <div className="afp-card-actions">
                      {/* Execute button for actionable cards */}
                      {isActionable && onExecuteCard && (
                        <Button
                          variant="outline"
                          iconOnly
                          size="sm"
                          className={`${isRunning ? "afp-execute-btn--running" : ""} ${isReady ? "afp-execute-btn--ready" : ""}`.trim()}
                          onClick={() => handleExecuteCard(card)}
                          disabled={isRunning}
                          title={isRunning ? t("common.running") : isReady ? t("affektra.runAgain") : t("affektra.execute")}
                          aria-label={isRunning ? t("common.running") : isReady ? t("affektra.runAgain") : t("affektra.execute")}
                        >
                          {isRunning ? (
                            <Loader2 size={14} className="afp-spin" />
                          ) : isReady ? (
                            "✓"
                          ) : (
                            <Play size={14} />
                          )}
                        </Button>
                      )}

                      {/* Expand/collapse button */}
                      <Button
                        variant="ghost"
                        iconOnly
                        size="sm"
                        onClick={() => setExpandedId((prev) => (prev === card.id ? null : card.id))}
                        title={isOpen ? t("common.collapse") : t("common.expand")}
                        aria-label={isOpen ? t("common.collapse") : t("common.expand")}
                      >
                        <ChevronDown
                          size={18}
                          strokeWidth={2}
                          style={{
                            transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
                            transition: "transform 140ms ease",
                          }}
                        />
                      </Button>
                    </div>
                  </div>

                  {/* Card Title */}
                  <div className="afp-card-title" title={card.title}>
                    <span className="afp-card-seq">{ci + 1}.</span> {card.title}
                  </div>

                  {/* Preview (only when collapsed) */}
                  {!isOpen && preview && (
                    <div className="afp-card-preview">{preview}</div>
                  )}

                  {/* Full body (when expanded) */}
                  {isOpen && card.body && (
                    <div className="afp-card-body">{card.body}</div>
                  )}

                  {/* Action data preview for actionable cards */}
                  {isOpen && card.action_data && (
                    <div className="afp-card-action-data">
                      {card.type === "add_board" && card.action_data.board_title && (
                        <div className="afp-action-item">
                          <span className="afp-action-label">Board:</span> {card.action_data.board_title}
                        </div>
                      )}
                      {card.type === "add_goal" && card.action_data.goal_title && (
                        <div className="afp-action-item">
                          <span className="afp-action-label">Goal:</span> {card.action_data.goal_title}
                        </div>
                      )}
                      {card.type === "analysis" && card.action_data.prompt_title && (
                        <div className="afp-action-item">
                          <span className="afp-action-label">Technique:</span> {card.action_data.prompt_title}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Result preview (if executed) */}
                  {cardState.result && (
                    <button
                      type="button"
                      className="afp-result-preview"
                      onClick={() => setDrawerCard({ ...card, status: cardState.status as any, result: cardState.result })}
                    >
                      {t("affektra.viewResult")} →
                    </button>
                  )}

                  {/* Feedback buttons */}
                  <div className="afp-card-feedback">
                    <Button
                      variant="ghost"
                      iconOnly
                      size="sm"
                      className="afp-fb-like"
                      aria-pressed={cardFeedbacks[card.id] === "like"}
                      onClick={() => handleFeedback(card, "like")}
                      title={t("affektra.helpful")}
                      aria-label={t("affektra.helpful")}
                    >
                      <ThumbsUp size={14} />
                    </Button>
                    <Button
                      variant="ghost"
                      iconOnly
                      size="sm"
                      className="afp-fb-dislike"
                      aria-pressed={cardFeedbacks[card.id] === "dislike"}
                      onClick={() => handleFeedback(card, "dislike")}
                      title={t("affektra.notHelpful")}
                      aria-label={t("affektra.notHelpful")}
                    >
                      <ThumbsDown size={14} />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Result Drawer */}
      {drawerCard && (
        <>
          <div className="afp-drawer-scrim" onClick={() => setDrawerCard(null)} />
          <div className="afp-drawer">
            <div className="afp-drawer-header">
              <div className="afp-drawer-title">
                <CardIcon type={drawerCard.type} size={18} />
                <span>{drawerCard.title}</span>
              </div>
              <Button
                variant="ghost"
                iconOnly
                size="sm"
                onClick={() => setDrawerCard(null)}
                aria-label={t("common.close")}
              >
                <X size={16} />
              </Button>
            </div>
            <div className="afp-drawer-body">
              {drawerCard.result ? (
                <div className="afp-drawer-content">{drawerCard.result}</div>
              ) : drawerCard.body ? (
                <div className="afp-drawer-content">{drawerCard.body}</div>
              ) : (
                <div className="afp-empty">{t("common.noContent")}</div>
              )}
            </div>
          </div>
        </>
      )}

      {/* Footer */}
      <div className="afp-footer" />

      <ConfirmDialog
        open={confirmClear}
        title={t("affektra.clearOutputsTitle")}
        message={t("affektra.clearOutputsMessage")}
        confirmLabel={t("common.clear")}
        variant="danger"
        onConfirm={() => {
          setConfirmClear(false);
          setExpandedId(null);
          setCardStates({});
          run("global", "clear_outputs");
        }}
        onCancel={() => setConfirmClear(false)}
      />
    </div>
  );
}
